import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Library } from '../../models/libraryModel/library.model';
import { Tab } from '../../models/libraryModel/tab.model';
import { Observable, map, switchMap, shareReplay } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LibraryInfo } from 'src/app/models/library-info';
import { ModifiedLibrary } from 'src/app/models/libraryModel/modifiedLibrary.model';
import { ExtendedRow } from 'src/app/models/libraryModel/extendedRow.model';
import { AuthAPIMService } from '../authAPIMService/authAPIM.service';
import { MsalService } from '@azure/msal-angular';

@Injectable({
  providedIn: 'root'
})
export class LibSvc {
  // Constructor to initialize services and dependencies
  constructor(private httpClient: HttpClient, private authAPIMService: AuthAPIMService, private msalService: MsalService) { }
  
  // Define the API URL for the library service
  apiUrlAPIM = `${environment.apiUrlAPIM}/api/pelotonlibrary${environment.pelotonRegion}/libraries`;
  apiUrlAzure = `${environment.apiUrlAzure}/api/graph/groups`;

  baseHeaders = new HttpHeaders({
    'Ocp-Apim-Subscription-Key': environment.subscriptionKey
  });

  // Function to get the list of libraries
  getLibraries(): Observable<LibraryInfo[]> {
    return this.authAPIMService.getAccessToken().pipe(
      switchMap(token => {
        const headers = this.baseHeaders
          .set('Authorization', `Bearer ${token}`);
        return this.httpClient.get<LibraryInfo[]>(`${this.apiUrlAPIM}?key=1`, { headers });
      }),
      map(libraries => {
        console.log(`[LibSvc] libraries list updated (${libraries?.length ?? 0} items)`);
        return libraries;
      })
    );
  }

  // Function to get a specific library by name
  getLibrary(libName: string, unitSet: string = ''): Observable<ModifiedLibrary> {
    return this.authAPIMService.getAccessToken().pipe(
      switchMap(token => {
        const headers = this.baseHeaders.set('Authorization', `Bearer ${token}`);

        const unitSetParam = unitSet ? `&unitSet=${encodeURIComponent(unitSet)}` : '';
        return this.httpClient.get<Library>(`${this.apiUrlAPIM}/${libName}?key=1${unitSetParam}`, { headers }).pipe(
          map(library => {
            const {unifiedRows, tabNames} = this.unifyTabsAndRows(library.tabs);
            return {
              tableName: library.tableName,
              columns: library.columns,
              rows: unifiedRows,
              tabNames: tabNames,
              nextId: unifiedRows.length + 1
            };
          })
        );
      })
    );
  }

  // Function to update a library
  putLibrary(library: ModifiedLibrary, automaticSort: boolean, unitSet: string = ''): Observable<any> {
    const orderedRows = 
      automaticSort ?
        [...library.rows].sort((a, b) => {
          const column = library.columns[0].columnName;
          if (a[column] < b[column]) return -1;
          if (a[column] > b[column]) return 1;
          return 0;
        })
      :
        [...library.rows];

    const payload = {
      tableName: library.tableName,
      columns: library.columns,
      tabs: this.separateTabs(orderedRows) //order by the first column of the library which seems to be the default order by setup on Peloton's Allez
    };

    return this.authAPIMService.getAccessToken().pipe(
      switchMap(token => {

        const headers = this.baseHeaders
          .set('Authorization', `Bearer ${token}`)
        
        if (!this.msalService.instance.getActiveAccount()?.idTokenClaims?.roles?.includes('Library.Admin'))
          throw new Error('Unauthorized. You are not allowed to make changes to this library.');

        const unitSetParam = unitSet ? `&unitSet=${encodeURIComponent(unitSet)}` : '';
        return this.httpClient.put(`${this.apiUrlAPIM}/${payload.tableName}?key=1${unitSetParam}`, payload, { headers }); 
      })
    );
  }

  // Private function to unify tabs and rows
  private unifyTabsAndRows(tabs: Tab[]): {unifiedRows: ExtendedRow[], tabNames: string[]} {
    const tabMap: { [key: string]: Tab } = {};

    // Combine rows from all tabs into a single list of rows
    tabs.forEach(tab => {
      if (!tabMap[tab.tabName]) {
        tabMap[tab.tabName] = { ...tab, rows: [...tab.rows] };
      } else {
        tabMap[tab.tabName].rows = [...tabMap[tab.tabName].rows, ...tab.rows];
      }
    });

    const rows: ExtendedRow[] = [];
    let id = 1;
    
    // Assign unique IDs to each row for easier handling and add them to the list of rows
    Object.values(tabMap).forEach(tab => {
      tab.rows.forEach(row => {
        rows.push({id: id++, tabName: tab.tabName, ...row});
      });
    });

    return {unifiedRows: Object.values(rows), tabNames: Object.values(tabMap).map(tab => tab.tabName)};
  }

  // Private function to separate rows into tabs
  private separateTabs(rows: ExtendedRow[]): Tab[] {
    const tabMap: { [key: string]: Tab } = {};
  
    // Group rows by their tab names
    rows.forEach(row => {
      if (!tabMap[row.tabName]) {
        tabMap[row.tabName] = { tabName: row.tabName, rows: [] };
      }
      const { id, tabName, ...rest } = row;
      tabMap[row.tabName].rows.push(rest);
    });
  
    return Object.values(tabMap);
  }  

  // Function to get user groups
  private libGroups$: Observable<string[]> | null = null;
  // Tracks which account the cached observable belongs to, so a login/logout
  // (or switching to a different account) invalidates the previous user's cache.
  private libGroupsUsername: string | null = null;

  public getLibGroups(): Observable<string[]> {
    const activeUsername = this.msalService.instance.getActiveAccount()?.username ?? null;

    if (!this.libGroups$ || this.libGroupsUsername !== activeUsername) {
      this.libGroupsUsername = activeUsername;
      this.libGroups$ = this.authAPIMService.getAccessToken().pipe(
        switchMap(token => {
          const headers = this.baseHeaders
            .set('Authorization', `Bearer ${token}`);
          return this.httpClient.get<string[]>(`${this.apiUrlAzure}?UPN=${activeUsername}&key=1`, { headers });
        }),
        map(groups => groups ?? []), // Normalize empty/204 responses so consumers never see null
        map(groups => {
          console.log(`[LibSvc] groups list updated (${groups.length} items)`);
          return groups;
        }),
        shareReplay(1) // Share a single in-flight/cached request across all callers for the same account
      );
    }

    return this.libGroups$;
  }

  public isSuperAdmin(): boolean {
    let isAdmin = false;
    this.getLibGroups().pipe(
      map(groups => groups?.includes('WvToolsAppAdmins')) //admin with full access to all WellView Tools
    ).subscribe(result => isAdmin = result);
    return isAdmin;
  }

  public isAdmin(): boolean {
    let isAdmin = false;
    this.getLibGroups().pipe(
      map(groups => groups?.includes('WvLibEditorAdmins')) //admin with access to WellView Library Editor
    ).subscribe(result => isAdmin = result);
    return isAdmin;
  }

}